import React from 'react';
import PaperclipIcon from './ui/PaperclipIcon';
import UploadingFileRow from './UploadingFileRow';
import { formatFileSize } from '../utils/formatFileSize';

/**
 * ONE file picked on an order form and uploaded to the pending store, before a
 * save attaches it to the order (R13, 2026-09-15). The entry comes from
 * `hooks/usePendingOrderUploads.js`: `{ key, file, state, fraction, error }`.
 *
 *   uploading → the green bar + % (`UploadingFileRow`, the delivery bar), with a
 *               ✕ that cancels and discards;
 *   failed    → the server's message, Retry (the same file) and ✕;
 *   uploaded  → name, size, **"Uploaded"** in green, and ✕ (deletes it now).
 *
 * 🔑 **"Uploaded" REPLACED "Staged", BY OWNER RULING.** Owner, 2026-09-15:
 * *"What does staged mean? If this means uploaded please chnge it to uploaded"*
 * / *"and in green"* — and the file now really IS on the server, so the word is
 * true. 🚨 **"Staged" must not come back on any surface.**
 *
 * 🎨 **THE GREEN IS MEASURED, NOT PICKED.** It is the SOLE text carrying the
 * state, so it owes 4.5:1 (WCAG 1.4.3). `text-green-700` (#15803d) is 5.02:1 on
 * white and 4.80:1 on gray-50 (#f9fafb, the order-level list's row). In dark mode
 * `index.css`'s `html.dark .text-green-700 { color: #86efac !important }` BEATS
 * the `dark:` class — the rendered colour is #86efac, 10.42:1 on the #1e293b both
 * rows really paint (`html.dark .bg-white` / `.bg-gray-50`); were that override
 * removed, `dark:text-green-400` (#4ade80) is 8.40:1. The rejected shade:
 * green-600 (#16a34a) is 3.30:1 on white and FAILS. That the colour is what renders
 * on screen is a BROWSER check.
 *
 * 🔒 **NO Download.** A pending file has no `order_files` row and no path the
 * authenticated `/uploads/*` router serves — it becomes downloadable once the
 * save attaches it.
 */
export const PENDING_FILE_UPLOADED_LABEL = 'Uploaded';
export const PENDING_FILE_UPLOADED_CLASS = 'text-green-700 dark:text-green-400';

function PendingUploadRow({ entry, onRemove, onRetry, className = '', testId = 'pending-upload' }) {
  const name = (entry.file && entry.file.name) || 'file';
  if (entry.state === 'uploading' || entry.state === 'failed') {
    return (
      <UploadingFileRow
        file={entry.file}
        fraction={entry.fraction}
        error={entry.state === 'failed' ? (entry.error || 'Upload failed') : null}
        onDismiss={entry.state === 'failed' ? () => onRemove(entry.key) : null}
        onRetry={entry.state === 'failed' && typeof onRetry === 'function' ? () => onRetry(entry.key) : null}
        onCancel={entry.state === 'uploading' ? () => onRemove(entry.key) : null}
        className={className}
        testId={testId}
      />
    );
  }
  return (
    <div className={`flex items-center justify-between ${className}`} data-testid={testId} data-upload-state="uploaded">
      <div className="flex items-center gap-1.5 min-w-0">
        <PaperclipIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
        <span className="text-gray-700 dark:text-gray-300 truncate" data-testid={`${testId}-name`}>{name}</span>
        <span className="text-gray-400 flex-shrink-0">({formatFileSize(Number(entry.file && entry.file.size) || 0)})</span>
        <span
          className={`${PENDING_FILE_UPLOADED_CLASS} text-[10px] font-medium uppercase tracking-wide flex-shrink-0`}
          data-testid={`${testId}-label`}
        >
          {PENDING_FILE_UPLOADED_LABEL}
        </span>
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(entry.key); }}
        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium flex-shrink-0 ml-2"
        aria-label={`Remove ${name}`}
        data-testid={`${testId}-remove`}
      >
        ✕
      </button>
    </div>
  );
}

export default PendingUploadRow;
